import { createColumnHelper } from "@tanstack/react-table";
import { moneyDisplay } from "@icraftx/shared/v1";
import { DocNumberLink } from "@/modules/core/components/DocNumberLink";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import { DeskDocListRowMenu } from "@/modules/shell/DeskDocListRowMenu";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import { stashListReturn } from "@/modules/shell/list-return-state";
import { relativeTimeLabel } from "@/modules/shell/relative-time";
import type { ReturnRow } from "@/shared/api/returns";

const columnHelper = createColumnHelper<DeskTableFeatures, ReturnRow>();

export type ReturnListCellActions = {
  listPath: string;
};

export function returnListColumns(
  companySlug: string,
  actions: ReturnListCellActions,
) {
  return columnHelper.columns([
    columnHelper.accessor("number", {
      id: "number",
      header: "Number",
      size: 120,
      cell: (info) => {
        const row = info.row.original;
        const docTo = `/c/${companySlug}/returns/${row.id}`;
        return (
          <DocNumberLink
            to={docTo}
            onClick={() => stashListReturn(actions.listPath, row.id)}
          >
            {info.getValue()}
          </DocNumberLink>
        );
      },
    }),
    columnHelper.accessor("sourceType", {
      id: "sourceType",
      header: "Source",
      size: 140,
      cell: (info) => {
        const row = info.row.original;
        const label =
          row.sourceType === "sales_order"
            ? "Sales order"
            : row.sourceType === "pos_ticket"
              ? "POS ticket"
              : row.sourceType.replaceAll("_", " ");
        const to =
          row.sourceType === "sales_order"
            ? `/c/${companySlug}/sales/orders/${row.sourceId}`
            : row.sourceType === "pos_ticket"
              ? `/c/${companySlug}/pos/tickets/${row.sourceId}`
              : null;
        if (!to) return label;
        return <DocNumberLink to={to}>{label}</DocNumberLink>;
      },
    }),
    columnHelper.accessor("lifecycle", {
      id: "lifecycle",
      header: "Status",
      size: 88,
      cell: (info) => (
        <LifecycleStatus
          value={info.getValue()}
          to={`/c/${companySlug}/returns/${info.row.original.id}`}
        />
      ),
    }),
    columnHelper.accessor("grossTotal", {
      id: "grossTotal",
      header: "Gross",
      size: 110,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor("settleMode", {
      id: "settleMode",
      header: "Settle",
      size: 100,
      enableSorting: false,
    }),
    columnHelper.accessor("restock", {
      id: "restock",
      header: "Restock",
      size: 90,
      enableSorting: false,
      cell: (info) => (info.getValue() ? "Yes" : "—"),
    }),
    columnHelper.accessor("updatedAt", {
      id: "updatedAt",
      header: "Updated",
      size: 88,
      enableSorting: false,
      cell: (info) => {
        const raw = info.getValue();
        return raw ? (
          <span className="text-xs text-[var(--color-muted)] tabular-nums">
            {relativeTimeLabel(raw)}
          </span>
        ) : (
          "—"
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 48,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const r = row.original;
        const docPath = `/c/${companySlug}/returns/${r.id}`;
        const sourceTo =
          r.sourceType === "sales_order"
            ? `/c/${companySlug}/sales/orders/${r.sourceId}`
            : r.sourceType === "pos_ticket"
              ? `/c/${companySlug}/pos/tickets/${r.sourceId}`
              : null;
        return (
          <DeskDocListRowMenu
            docPath={docPath}
            tabLinks={
              sourceTo
                ? [
                    {
                      label: "Open source",
                      to: sourceTo,
                    },
                  ]
                : []
            }
          />
        );
      },
    }),
  ]);
}
